import { removeTextTags } from "@domestika-dev/prudencio/dist/utils/textUtils";
import {
  getLocaleMaxScheduleDate,
  getLocaleMinScheduleDate,
  isInvalidMaxScheduleDate,
  isInvalidMinScheduleDate,
  updateScheduleDate,
  updateScheduleTime,
} from "../utils/dateUtils";
import {
  isMaxLengthLiveSessionTitle,
  isMinLengthLiveSessionDescription,
  isMinLengthLiveSessionTitle,
  isValidLiveSessionDescription,
  isValidLiveSessionTitle,
} from "../utils/textUtils";
import * as ScheduleFormFields from "../constants/liveSessionScheduleFormFields";
import { config } from "../config";
import { scheduleFormFieldsInitialValueMapper } from "../mappers/liveSessionScheduleForm";

const formValidation = {
  [ScheduleFormFields.TITLE]: (value) => isValidLiveSessionTitle(value, true),
  [ScheduleFormFields.DATE]: (value) => !!value?.length,
  [ScheduleFormFields.TIME]: (value) => value?.length && value.split(":").length === 2,
  [ScheduleFormFields.SCHEDULED_AT]: (value) =>
    value && !isInvalidMinScheduleDate(value) && !isInvalidMaxScheduleDate(value),
  [ScheduleFormFields.LANGUAGE]: (value) => !!value?.length,
  [ScheduleFormFields.DESCRIPTION]: (value) => isValidLiveSessionDescription(removeTextTags(value)),
};

export class LiveSessionScheduleForm {
  constructor({ initialData, previousState, skipFieldValidations }) {
    if (previousState) {
      this.fields = { ...previousState.fields };
    } else {
      this.initializeFields({ initialData, skipFieldValidations });
    }

    this.updateFormValidity();
  }

  initializeFields({ initialData, skipFieldValidations }) {
    this.fields = {};

    this.updateTitle(
      scheduleFormFieldsInitialValueMapper[ScheduleFormFields.TITLE](initialData),
      skipFieldValidations
    );
    this.updateDate(
      scheduleFormFieldsInitialValueMapper[ScheduleFormFields.DATE](initialData),
      skipFieldValidations
    );
    this.updateTime(
      scheduleFormFieldsInitialValueMapper[ScheduleFormFields.TIME](initialData),
      skipFieldValidations
    );
    this.updateLanguage(
      scheduleFormFieldsInitialValueMapper[ScheduleFormFields.LANGUAGE](initialData),
      skipFieldValidations
    );
    this.updateDescription(
      scheduleFormFieldsInitialValueMapper[ScheduleFormFields.DESCRIPTION](initialData),
      skipFieldValidations
    );
  }

  updateFormValidity() {
    this.isFormInvalid = Object.entries(formValidation).some(
      ([field, validationFn]) => !validationFn(this.fields[field]?.value)
    );
  }

  validateTitle(value) {
    const isValid = formValidation[ScheduleFormFields.TITLE](value);
    const isMinLength = isMinLengthLiveSessionTitle(value, true);
    const isMaxLength = isMaxLengthLiveSessionTitle(value, true);

    let errorMessage = "";
    const errorMessageValues = {};
    if (isMinLength) {
      errorMessage = "live_sessions.schedule_new.title_input.error_min_length";
      errorMessageValues.length = config.MIN_LIVE_SESSION_TITLE_LENGTH;
    }

    this.fields[ScheduleFormFields.TITLE] = {
      ...this.fields[ScheduleFormFields.TITLE],
      hasError: !isValid,
      lengthError: isMinLength || isMaxLength,
      errorMessage,
      errorMessageValues,
    };
  }

  updateTitle(value, skipFieldValidations) {
    this.fields[ScheduleFormFields.TITLE] = { value };
    if (!skipFieldValidations) {
      this.validateTitle(value);
    }

    this.updateFormValidity();
    return this;
  }

  validateScheduledAt(value) {
    const isValid = formValidation[ScheduleFormFields.SCHEDULED_AT](value);
    const isMinInvalid = isInvalidMinScheduleDate(value);
    const isMaxInvalid = isInvalidMaxScheduleDate(value);

    this.fields[ScheduleFormFields.SCHEDULED_AT] = {
      ...this.fields[ScheduleFormFields.SCHEDULED_AT],
      hasError: !isValid,
      minDateError: isMinInvalid,
      maxDateError: isMaxInvalid,
    };
  }

  updateScheduledAt({ dateValue, timeValue }, skipFieldValidations) {
    if (!dateValue && !timeValue) {
      if (!this.fields[ScheduleFormFields.SCHEDULED_AT]) {
        this.fields[ScheduleFormFields.SCHEDULED_AT] = {};
      }
      return;
    }

    const scheduledDate = this.fields[ScheduleFormFields.SCHEDULED_AT]?.value;
    let newScheduledDate = null;
    if (dateValue) {
      newScheduledDate = updateScheduleDate(dateValue, scheduledDate);
    } else {
      newScheduledDate = updateScheduleTime(timeValue, scheduledDate);
    }

    this.fields[ScheduleFormFields.SCHEDULED_AT] = { value: newScheduledDate };
    if (!skipFieldValidations) {
      this.validateScheduledAt(newScheduledDate);
    }
  }

  validateDate(value) {
    const isValid = formValidation[ScheduleFormFields.DATE](value);
    const { hasError, minDateError, maxDateError } = this.fields[ScheduleFormFields.SCHEDULED_AT];

    let errorMessage = "";
    const errorMessageValues = {};
    if (minDateError) {
      errorMessage = "live_sessions.schedule_new.date_input.error_min_date";
      errorMessageValues.min_date = getLocaleMinScheduleDate();
    } else if (maxDateError) {
      errorMessage = "live_sessions.schedule_new.date_input.error_max_date";
      errorMessageValues.max_date = getLocaleMaxScheduleDate();
    }

    this.fields[ScheduleFormFields.DATE] = {
      ...this.fields[ScheduleFormFields.DATE],
      hasError: !isValid || hasError,
      errorMessage,
      errorMessageValues,
    };

    if (!hasError) {
      this.fields[ScheduleFormFields.TIME] = {
        ...this.fields[ScheduleFormFields.TIME],
        hasError: false,
        errorMessage: "",
        errorMessageValues: {},
      };
    }
  }

  updateDate(value, skipFieldValidations) {
    this.updateScheduledAt({ dateValue: value }, skipFieldValidations);
    this.fields[ScheduleFormFields.DATE] = { value };
    if (!skipFieldValidations) {
      this.validateDate(value);
    }

    this.updateFormValidity();
    return this;
  }

  validateTime(value) {
    const isValid = formValidation[ScheduleFormFields.TIME](value);
    const { hasError, minDateError, maxDateError } = this.fields[ScheduleFormFields.SCHEDULED_AT];

    let errorMessage = "";
    const errorMessageValues = {};
    if (minDateError) {
      errorMessage = "live_sessions.schedule_new.time_input.error_min_time";
      errorMessageValues.min_date = getLocaleMinScheduleDate();
    } else if (maxDateError) {
      errorMessage = "live_sessions.schedule_new.time_input.error_max_time";
      errorMessageValues.max_date = getLocaleMaxScheduleDate();
    }

    this.fields[ScheduleFormFields.TIME] = {
      ...this.fields[ScheduleFormFields.TIME],
      hasError: !isValid || hasError,
      errorMessage,
      errorMessageValues,
    };

    if (!hasError) {
      this.fields[ScheduleFormFields.DATE] = {
        ...this.fields[ScheduleFormFields.DATE],
        hasError: false,
        errorMessage: "",
        errorMessageValues: {},
      };
    }
  }

  updateTime(value, skipFieldValidations) {
    this.updateScheduledAt({ timeValue: value }, skipFieldValidations);
    this.fields[ScheduleFormFields.TIME] = { value };
    if (!skipFieldValidations) {
      this.validateTime(value);
    }

    this.updateFormValidity();
    return this;
  }

  validateLanguage(value) {
    const isValid = formValidation[ScheduleFormFields.LANGUAGE](value);
    this.fields[ScheduleFormFields.LANGUAGE] = {
      ...this.fields[ScheduleFormFields.LANGUAGE],
      hasError: !isValid,
      errorMessage: isValid ? "" : "live_sessions.schedule_new.language_input.error_required",
    };
  }

  updateLanguage(value, skipFieldValidations) {
    this.fields[ScheduleFormFields.LANGUAGE] = { value };
    if (!skipFieldValidations) {
      this.validateLanguage(value);
    }

    this.updateFormValidity();
    return this;
  }

  validateDescription(value) {
    const isValid = formValidation[ScheduleFormFields.DESCRIPTION](value);
    const isMinLength = isMinLengthLiveSessionDescription(value, true);

    let errorMessage = "";
    const errorMessageValues = {};
    if (isMinLength) {
      errorMessage = "live_sessions.schedule_new.description_input.error_min_length";
      errorMessageValues.length = config.MIN_LIVE_SESSION_DESCRIPTION_LENGTH;
    }

    this.fields[ScheduleFormFields.DESCRIPTION] = {
      ...this.fields[ScheduleFormFields.DESCRIPTION],
      hasError: !isValid,
      errorMessage,
      errorMessageValues,
    };
  }

  updateDescription(value, skipFieldValidations) {
    this.fields[ScheduleFormFields.DESCRIPTION] = { value };
    if (!skipFieldValidations) {
      this.validateDescription(value);
    }

    this.updateFormValidity();
    return this;
  }
}
